import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Typography, Avatar, Collapse, Divider } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useState, useCallback } from 'react';

import { ExpandMore } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-issue-list-expand-button';
import { Comment } from '@eng-mono/atlassian/jira/mojira-bugtracker/util-issue-list-fe';
import { MojiraBugtrackerBackendPaths } from '@eng-mono/atlassian/jira/mojira-bugtracker/util-resolver';
import { useBackendAdapter } from '@eng-mono/atlassian/shared/frontend-ui-backend-adapter';

import { CommentBody } from './comments-body';

interface CommentDisplayProps {
  issueKey: string;
}

export function CommentDisplay({ issueKey }: CommentDisplayProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [expandedComments, setExpandedComments] = useState<
    Record<string, boolean>
  >({});

  const { invoke } = useBackendAdapter();

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await invoke<Comment[]>(
        MojiraBugtrackerBackendPaths.issueCommentGet,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            authorization: 'Basic MTox',
          },
          issueKey: issueKey,
        }
      );

      const parsedComments = response;

      const reversedComments = parsedComments.slice().reverse();

      setComments(reversedComments);

      const initialExpandedState: Record<string, boolean> = {};
      reversedComments.forEach((comment) => {
        initialExpandedState[comment.id] = true;
      });
      setExpandedComments(initialExpandedState);
    } finally {
      setLoading(false);
    }
  }, [invoke, issueKey]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleExpandClick = (commentId: string) => {
    setExpandedComments((prevState) => ({
      ...prevState,
      [commentId]: !prevState[commentId],
    }));
  };

  return loading ? (
    <CircularProgress />
  ) : (
    <div>
      {comments.length ? (
        comments.map((comment) => (
          <Box key={comment.id} sx={{ marginBottom: 2 }}>
            <Divider sx={{ marginBottom: 2 }} />

            {/* Comment Header */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}
            >
              <Box
                sx={{
                  width: '40px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <ExpandMore
                  expanded={expandedComments[comment.id].toString() ?? ''}
                  onClick={() => handleExpandClick(comment.id)}
                  aria-expanded={!!expandedComments[comment.id]}
                  aria-label={`comment ${comment.id} show ${expandedComments[comment.id] ? 'less' : 'more'}`}
                >
                  <ExpandMoreIcon />
                </ExpandMore>
              </Box>

              {/* Avatar and Author Information */}
              <Avatar
                src={comment.author.avatarUrls.small}
                alt={comment.author.displayName}
                sx={{ marginRight: 2 }}
              />
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {comment.author.displayName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ marginLeft: 1, color: 'text.secondary' }}
              >
                {new Date(comment.created).toLocaleString()}
              </Typography>
            </Box>

            {/* Collapsible Comment Body */}
            <Collapse
              in={expandedComments[comment.id]}
              timeout="auto"
              unmountOnExit
            >
              <Box sx={{ paddingLeft: '48px', marginBottom: 2 }}>
                <CommentBody comment={comment} />
              </Box>
            </Collapse>

            <Box sx={{ clear: 'both' }} />
          </Box>
        ))
      ) : (
        <Typography>No public text comments available.</Typography>
      )}
    </div>
  );
}
